import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import { ReturnExpiryThresholdControllerService } from '@app/api/stock-service/api/returnExpiryThresholdController.service';

@Component({
  selector: 'app-return-expiry-threshold',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    InputNumberModule,
    ToggleSwitchModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './return-expiry-threshold.component.html',
  styleUrl: './return-expiry-threshold.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnExpiryThresholdComponent implements OnInit {
  private readonly api = inject(ReturnExpiryThresholdControllerService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.inventory.return_expiry_threshold.view',
    );
  });

  canEdit = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.inventory.return_expiry_threshold.edit',
    );
  });

  loading = signal(false);
  saving = signal(false);
  minRemainingDays = signal(0);
  enabled = signal(true);
  configId = signal<number | undefined>(undefined);

  ngOnInit(): void {
    if (!this.canView()) {
      return;
    }
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getCurrent().subscribe({
      next: (cfg) => {
        this.configId.set(cfg.id);
        this.minRemainingDays.set(cfg.minRemainingDays ?? 0);
        this.enabled.set(cfg.enabled !== false);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail: this.translate.instant(
            'return_expiry_threshold.messages.load_failed',
          ),
        });
      },
    });
  }

  save(): void {
    if (!this.canEdit()) {
      return;
    }
    const days = this.minRemainingDays();
    if (days == null || days < 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.messages.warning'),
        detail: this.translate.instant(
          'return_expiry_threshold.messages.invalid_days',
        ),
      });
      return;
    }
    this.saving.set(true);
    this.api
      .update({
        minRemainingDays: days,
        enabled: this.enabled(),
      })
      .subscribe({
        next: (cfg) => {
          this.configId.set(cfg.id);
          this.saving.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.messages.success'),
            detail: this.translate.instant(
              'return_expiry_threshold.messages.save_success',
            ),
          });
        },
        error: () => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant(
              'return_expiry_threshold.messages.save_failed',
            ),
          });
        },
      });
  }
}
